import React from 'react';

import { Variable } from '../../common/Variable';
import type { MailTemplate } from '../components/SciMailTemplates';

type MailTemplateContent = Omit<MailTemplate, 'content'> & { content: React.ReactNode };

export const transactionMailTemplates: MailTemplateContent[] = [
  {
    id: 1,
    titre: 'Bien à vendre dans l\'immeuble et à proposer en avant première aux SCIs présentes dans la copro',
    apercu: 'Information sur un bien à vendre dans la copropriété, proposé en priorité aux SCIs présentes...',
    objet: 'Information – Bien à vendre dans votre immeuble',
    content: (
      <div className="space-y-4">
        <div>
          <p className="font-semibold text-gray-700">Objet :</p>
          <p className="mt-1">Information – Bien à vendre dans votre immeuble</p>
        </div>

        <div className="space-y-4 pt-4">
          <p>Bonjour,</p>

          <p>
            Nous vous informons qu'un appartement / un parking / une cave situé au sein de votre copropriété <strong>[NOM_DE_LA_COPRO], [ADRESSE_DE_LA_COPRO],</strong> fait actuellement l'objet d'un mandat de vente confié à notre service Transaction.
          </p>

          <p>
            En tant que SCI présente dans l'immeuble, nous souhaitons vous proposer cette opportunité en avant-première :
          </p>

          <ul className="ml-6 list-disc space-y-2">
            <li>Si vous envisagez un changement de logement tout en restant dans la résidence</li>
            <li>Si vous souhaitez acquérir un lot supplémentaire (investissement locatif, agrandissement, logement pour un proche, etc.)</li>
            <li>Ou si vous connaissez une personne de votre entourage susceptible d'être intéressée par le secteur</li>
          </ul>

          <p>
            Nous serons ravis de vous présenter ce bien et de vous communiquer ses caractéristiques (typologie, surface, prix, modalités de visite) lors d'un simple échange téléphonique, sans engagement de votre part.
          </p>

          <p>
            Si vous souhaitez obtenir davantage d'informations, vous pouvez nous contacter aux coordonnées ci-dessous.
          </p>

          <p>Bien cordialement,</p>

          <p>
            [Votre Nom]
            <br />
            Responsable Transactions
            <br />
            [Téléphone]
            <br />
            [Email]
          </p>

          <p className="mt-6 text-xs text-gray-600 italic">
            Ce message vous est adressé par <Variable>[NOM_SOUSTRAITANT_MAIL]</Variable>, <Variable>[ADRESSE_SOUSTRAITANT_MAIL]</Variable>, <Variable>[REPRESENTANT_LEGAL_SOUSTRAITANT_MAIL]</Variable>, <Variable>[CONTACT_SOUSTRAITANT_MAIL]</Variable>. Si vous souhaitez ne plus recevoir d'email concernant l'objet précis de cet envoi, vous pouvez vous désinscrire <Variable>[LIEN_DE_DESINSCRIPTION]</Variable>.
          </p>
        </div>
      </div>
    ),
  },
  {
    id: 2,
    titre: 'Proposition service analyse patrimonial',
    apercu: 'Étude personnalisée de rentabilité et stratégie immobilière pour votre SCI...',
    objet: 'Étude personnalisée de rentabilité et stratégie immobilière',
    content: (
      <div className="space-y-4">
        <div>
          <p className="font-semibold text-gray-700">Objet :</p>
          <p className="mt-1">Étude personnalisée de rentabilité et stratégie immobilière</p>
        </div>

        <div className="space-y-4 pt-4">
          <p>Bonjour,</p>

          <p>
            En tant que propriétaire d'un ou plusieurs biens situés dans la copropriété <strong>[NOM_DE_LA_COPRO], [ADRESSE_DE_LA_COPRO],</strong> nous souhaitons attirer votre attention sur l'importance d'une réflexion stratégique autour de la rentabilité de vos investissements.
          </p>

          <p>
            Nous vous proposons de réaliser pour votre SCI une étude approfondie afin d'évaluer précisément la rentabilité actuelle de votre patrimoine immobilier dans cette copropriété.
          </p>

          <p>
            Cette analyse prendra en compte des critères internes tels que le futur Plan Pluriannuel de Travaux (PPPT) et la performance énergétique collective (DPE collectif), ainsi que des éléments externes tels que l'échéance prochaine des dispositifs fiscaux de type Pinel dans le secteur, susceptibles d'impacter négativement les prix et les tendances générales de marché observées dans le quartier.
          </p>

          <p>
            En fonction des résultats de cette étude, vous pourriez envisager de conserver ou de vendre certains biens à bon prix aujourd'hui et réinvestir dans des opportunités à plus forte rentabilité sur le long terme.
          </p>

          <p>
            Je reste à votre entière disposition pour toute question ou pour organiser un rendez-vous afin de vous exposer plus précisément cette démarche.
          </p>

          <p>Bien cordialement,</p>

          <p>
            [Votre Nom]
            <br />
            Responsable Transactions
            <br />
            [Téléphone]
            <br />
            [Email]
          </p>

          <p className="mt-6 text-xs text-gray-600 italic">
            Ce message vous est adressé par <Variable>[NOM_SOUSTRAITANT_MAIL]</Variable>, <Variable>[ADRESSE_SOUSTRAITANT_MAIL]</Variable>, <Variable>[REPRESENTANT_LEGAL_SOUSTRAITANT_MAIL]</Variable>, <Variable>[CONTACT_SOUSTRAITANT_MAIL]</Variable>. Si vous souhaitez ne plus recevoir d'email concernant l'objet précis de cet envoi, vous pouvez vous désinscrire <Variable>[LIEN_DE_DESINSCRIPTION]</Variable>.
          </p>
        </div>
      </div>
    ),
  },
  {
    id: 3,
    titre: 'Informations sur votre copropriété',
    apercu: 'Présentation complète de votre immeuble incluant DPE et historique des ventes...',
    objet: 'Informations complètes sur votre copropriété et son environnement',
    content: (
      <div className="space-y-4">
        <div>
          <p className="font-semibold text-gray-700">Objet :</p>
          <p className="mt-1">Informations complètes sur votre copropriété et son environnement</p>
        </div>

        <div className="space-y-4 pt-4">
          <p>Bonjour,</p>

          <p>
            Dans le cadre du Diagnostic de Performance Énergétique (DPE) collectif et de l'élaboration du Plan Pluriannuel de Travaux (PPPT) pour la copropriété <strong>[NOM_DE_LA_COPRO], [ADRESSE_DE_LA_COPRO],</strong> nous collectons actuellement des informations clés telles que les derniers DPE réalisés et l'historique des ventes immobilières dans l'immeuble et son environnement proche.
          </p>

          <p>
            Dans ce cadre pour votre SCI, nous vous proposons de recevoir, sur simple demande, une présentation complète et détaillée de votre immeuble et de son environnement immédiat.
          </p>

          <p>Cette présentation inclut notamment :</p>

          <ul className="ml-6 list-disc space-y-2">
            <li>Les derniers diagnostics de performance énergétique (DPE) réalisés dans la copropriété,</li>
            <li>Un historique des ventes réalisées ces 4 dernières années dans la copropriété et aux alentours immédiats.</li>
          </ul>

          <p>
            Ces informations vous permettront d'évaluer précisément la valeur actuelle de vos biens et leur potentiel futur.
          </p>

          <p>
            Nous restons à votre disposition pour tout complément d'information ou pour vous transmettre rapidement cette présentation personnalisée.
          </p>

          <p>Bien cordialement,</p>

          <p>
            [Votre Nom]
            <br />
            Responsable Transactions
            <br />
            [Téléphone]
            <br />
            [Email]
          </p>

          <p className="mt-6 text-xs text-gray-600 italic">
            Ce message vous est adressé par <Variable>[NOM_SOUSTRAITANT_MAIL]</Variable>, <Variable>[ADRESSE_SOUSTRAITANT_MAIL]</Variable>, <Variable>[REPRESENTANT_LEGAL_SOUSTRAITANT_MAIL]</Variable>, <Variable>[CONTACT_SOUSTRAITANT_MAIL]</Variable>. Si vous souhaitez ne plus recevoir d'email concernant l'objet précis de cet envoi, vous pouvez vous désinscrire <Variable>[LIEN_DE_DESINSCRIPTION]</Variable>.
          </p>
        </div>
      </div>
    ),
  },
  {
    id: 4,
    titre: 'Opportunité de vente - Anticipation PPPT',
    apercu: "Réflexion sur l'opportunité de vendre en anticipation du PPPT et impact DPE...",
    objet: 'Opportunité de vente à considérer - Anticipation du PPPT et impact du DPE collectif',
    content: (
      <div className="space-y-4">
        <div>
          <p className="font-semibold text-gray-700">Objet :</p>
          <p className="mt-1">Opportunité de vente à considérer - Anticipation du PPPT et impact du DPE collectif</p>
        </div>

        <div className="space-y-4 pt-4">
          <p>Bonjour,</p>

          <p>
            En tant que propriétaire de biens dans la copropriété <strong>[NOM_DE_LA_COPRO], [ADRESSE_DE_LA_COPRO]</strong>, il nous paraît pertinent de vous inviter à réfléchir à l'opportunité de vendre tout ou partie de votre patrimoine immobilier dans cet immeuble.
          </p>

          <p>
            En effet, le futur Plan Pluriannuel de Travaux (PPPT), actuellement en cours d'élaboration, associé à la classe énergétique du DPE collectif de l'immeuble <mark className="bg-yellow-200">pourrait peut-être avoir une incidence sur la rentabilité locative et la valeur future des appartements concernés.</mark>
          </p>

          <p>
            Afin que vous puissiez évaluer précisément l'impact potentiel sur vos biens, nous pouvons réaliser, à votre demande, une étude approfondie tenant compte de ces éléments.
          </p>

          <p>
            Nous restons à votre disposition pour toute question ou prise de rendez-vous afin de discuter plus en détail de cette possibilité.
          </p>

          <p>Bien cordialement,</p>

          <p>
            [Votre Nom]
            <br />
            Responsable Transactions
            <br />
            [Téléphone]
            <br />
            [Email]
          </p>

          <p className="mt-6 text-xs text-gray-600 italic">
            Ce message vous est adressé par <Variable>[NOM_SOUSTRAITANT_MAIL]</Variable>, <Variable>[ADRESSE_SOUSTRAITANT_MAIL]</Variable>, <Variable>[REPRESENTANT_LEGAL_SOUSTRAITANT_MAIL]</Variable>, <Variable>[CONTACT_SOUSTRAITANT_MAIL]</Variable>. Si vous souhaitez ne plus recevoir d'email concernant l'objet précis de cet envoi, vous pouvez vous désinscrire <Variable>[LIEN_DE_DESINSCRIPTION]</Variable>.
          </p>
        </div>
      </div>
    ),
  },
];
